/**
 * Secret Key for decrypting AES-256-GCM binary payload over the network.
 * Can be overridden via REACT_APP_DATA_ENCRYPTION_KEY.
 */
export const DATA_ENCRYPTION_KEY =
  process.env.REACT_APP_DATA_ENCRYPTION_KEY || 'NiftySutra@2026$DataProtect!!888';

/**
 * API Configuration
 */
export const API_CONFIG = {
  BASE_URL: '/api/data',
  ENDPOINTS: {
    ANALYTICS: '/analytics',
    ANALYTICS_SUMMARY: '/analytics-summary',
    ANALYTICS_SUMMARY_ARCHIVE: '/analytics-summary-archive',
    ANALYTICS_DETAIL: (date: string) => `/analytics/${date}`,
    ACCUMULATED_STOCKS: (date: string) => `/AccumulatedStocks/${date}`,
    ACCUMULATION_COUNT: (date: string) => `/AccumulationCount/${date}`,
    VOLUME_SHOCKERS: (date: string) => `/VolumeShockers/${date}`,
    EQUITY_DELIVERY: (date: string) => `/EquityDelivery/${date}`,
    OVERSOLD: (date: string) => `/OverSold/${date}`,
    OVERBOUGHT: (date: string) => `/Overbought/${date}`,
    EMA21_50_200_STOCKS: (date: string) => `/Ema21_50_200_Stocks/${date}`,
    BREAKOUTS: (date: string) => `/Breakouts/${date}`,
    APPROACHING_BREAKOUTS: (date: string) => `/ApproachingBreakouts/${date}`,
    SECTOR_SUMMARY: '/sector-summary',
    SECTOR_SUMMARY_ARCHIVE: '/sector-summary-archive',
    SECTOR_STOCK_SIGNALS: (date: string) => `/SectorStockSignals/${date}`,
    STOCKS_SNAPSHOT_LATEST: '/stocks-snapshot-latest',
    STOCKS_SNAPSHOT_DAILY: (date: string) => `/stocks-snapshot/${date}`,
    MARKET_DRAWDOWN_LATEST: '/market-drawdown-distribution',
    MARKET_DRAWDOWN_DAILY: (date: string) => `/market-drawdown/${date}`,
    SECTOR_UNIVERSE: '/sector-universe',
    ALL_STOCKS: '/allstocks',
    STOCK_HISTORY: (symbol: string) => `/${symbol}`,
    INDEX_HISTORY: (fileSafeIndexName: string) => `/indices/${fileSafeIndexName}`,
    INDEX_LATEST: '/indices-latest',
    INDEX_DAILY: (date: string) => `/indices/daily/${date}`,
    VOLUME_STREAKS: '/volume-streaks',
    PRICE_MOVERS: '/price-movers',
    CORPORATE_FEEDS_LATEST: '/corporate-feeds/latest',
    CORPORATE_FEEDS_DAILY: (date: string) => `/corporate-feeds/daily/${date}`,
    CORPORATE_FEEDS_METADATA: '/corporate-feeds/metadata',
    CORPORATE_FEEDS_COMPANY: (symbol: string) => `/corporate-feeds/companies/${symbol}`,
    CORPORATE_FEEDS_CATALYSTS_SUMMARY: '/corporate-feeds/catalysts-summary',
    FII_DII_LATEST: '/fii-dii/latest',
    FII_DII_HISTORY: '/fii-dii/history',
    FII_DII_MONTHLY: '/fii-dii/monthly',
    MF_FLOW_LATEST: '/mf-flow/latest',
    MF_FLOW_HISTORY: '/mf-flow/history',
    MF_FLOW_MONTHLY: '/mf-flow/monthly',
    CALCULATOR_STATUS: '/calculator-status',
  },
  RETRY: {
    MAX_ATTEMPTS: 3,
    INITIAL_DELAY: 1000,
  },
} as const;

/**
 * Chart Color Schemes
 */
export const CHART_COLORS = {
  POSITIVE: '#4caf50',
  NEGATIVE: '#f44336',
  BLUE: '#2196f3',
  ORANGE: '#ff9800',
  PURPLE: '#9c27b0',
  BROWN: '#795548',
  GREY: '#9e9e9e',
  PINK: '#e91e63',
  NIFTY_ACC: '#de6f00',
  NIFTY_VOL: '#006fde',
  NIFTY_RATIO: '#673ab7',
} as const;

/**
 * Delivery Percentage Thresholds
 */
export const DELIVERY_THRESHOLDS = {
  HIGH: 80,
  MEDIUM: 60,
  LOW: 50,
} as const;

/**
 * CSS Class Names for Delivery Thresholds
 */
export const DELIVERY_ROW_CLASSES = {
  HIGH: 'row-pink',
  MEDIUM: 'row-green',
  LOW: 'row-orange',
} as const;

/**
 * EMA Periods
 */
export const EMA_PERIODS = {
  SHORT: 21,
  MEDIUM: 50,
  LONG: 200,
} as const;

/**
 * Date Range Defaults
 */
export const DATE_RANGE_DEFAULTS = {
  DAYS_BACK: 365,
  LOCALE: 'en-IN',
} as const;

/**
 * Chart Configuration Defaults
 */
export const CHART_CONFIG = {
  HEIGHT: 400,
  ANIMATION_DURATION: 300,
  TOOLTIP_TRIGGER: 'axis',
  LEGEND_POSITION: 'top',
} as const;

/**
 * Accumulation Day Thresholds
 */
export const ACCUMULATION = {
  MIN_DAYS: 1,
  DISPLAY_THRESHOLD: 10,
  RECENT_SESSIONS: 20,
} as const;

/**
 * External Links
 */
export const EXTERNAL_LINKS = {
  SCREENER: (symbol: string) => `https://www.screener.in/company/${symbol}`,
  TRADING_VIEW: (symbol: string) => `https://www.tradingview.com/chart/?symbol=${symbol}`,
} as const;

/**
 * UI Messages
 */
export const UI_MESSAGES = {
  LOADING: 'Loading...',
  COPIED: 'Copied to clipboard!',
  ERROR_FETCH: 'Failed to fetch data',
  NO_DATA: 'No data available',
} as const;

/**
 * Snackbar Configuration
 */
export const SNACKBAR_CONFIG = {
  AUTO_HIDE_DURATION: 2000,
  ANCHOR_ORIGIN: { vertical: 'top', horizontal: 'right' } as const,
} as const;

/**
 * Stock Symbol Prefix
 */
export const STOCK_PREFIX = 'NSE:' as const;

/**
 * Info Messages for UI
 */
export const INFO_MESSAGES = {
  DELIVERY_HIGH: '* Pink color indicates stocks with 80%+ delivery',
  DELIVERY_MEDIUM: '* Green color indicates stocks with 60%-80% delivery',
  DELIVERY_LOW: '* Orange color indicates stocks with 50%-60% delivery',
  STOCK_ABOVE_ALL_EMA: '* Pink color indicates stocks with price above 21, 50 and 200 EMA.',
  STOCK_ABOVE_21_EMA: '* Green color indicates stocks with price above 21 EMA.',
  STOCK_EMA_STACKED: '* Orange color indicates stocks with 21 EMA above 50 EMA and 50 EMA above 200 EMA.',
  ACCUMULATION_DAY_MARKER: '* Gold dots on the mini chart mark sessions where accumulation was detected.',
} as const;

export const EMA_INFO_MESSAGES = [
  INFO_MESSAGES.STOCK_ABOVE_ALL_EMA,
  INFO_MESSAGES.STOCK_ABOVE_21_EMA,
  INFO_MESSAGES.STOCK_EMA_STACKED,
] as const;

export const ACCUMULATION_INFO_MESSAGES = [
  INFO_MESSAGES.STOCK_ABOVE_ALL_EMA,
  INFO_MESSAGES.STOCK_ABOVE_21_EMA,
  INFO_MESSAGES.STOCK_EMA_STACKED,
  INFO_MESSAGES.ACCUMULATION_DAY_MARKER,
] as const;
